import type { Period } from "../components/molecules/FilterContextProvider.shared";
import type { SessionFilter } from "../types/types";

import { toUTC } from "@/shared/utils";

/**
 * Ermittelt aus Period + optionalen Custom-Dates den SessionFilter,
 * wie ihn das Rust-Backend erwartet.
 *
 * @param period
 *
 * @param customStart
 *
 * @param customEnd
 *
 * @returns {SessionFilter} Date range filter for backend
 *
 */
export const getDateRange = (
  period: Period,
  customStart?: Date | null,
  customEnd?: Date | null,
): SessionFilter => {
  // Custom – benutze exakt die gewählten Daten
  if (period === "Custom") {
    return {
      endDate: customEnd ? toUTC(customEnd) : null,
      startDate: customStart ? toUTC(customStart) : null,
    };
  }

  // Alltime – keine Grenzen
  if (period === "Alltime") {
    return { endDate: null, startDate: null };
  }

  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (period) {
    case "Today": {
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      break;
    }

    case "This Week": {
      const d = now.getDay(); // 0 = So … 6 = Sa
      const offset = d === 0 ? 6 : d - 1; // zurück bis Montag
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset, 0, 0, 0, 0);
      endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - offset + 6,
        23,
        59,
        59,
        999,
      );
      break;
    }

    case "Last Week": {
      const d = now.getDay();
      const offset = d === 0 ? 13 : d + 6; // vorletzter Montag
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset, 0, 0, 0, 0);
      endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - offset + 6,
        23,
        59,
        59,
        999,
      );
      break;
    }

    case "This Month": {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    }

    case "Last Month": {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      break;
    }

    case "This Year": {
      startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    }

    case "Last Year": {
      startDate = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      endDate = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      break;
    }

    default: {
      // Fallback (sollte nie eintreten): keine Filterung
      return { endDate: null, startDate: null };
    }
  }

  return { endDate: toUTC(endDate), startDate: toUTC(startDate) };
};
